// auditpm-v6
// ─────────────────────────────────────────────────────────────────────────────
// CT17 man-day calculation — SINGLE SOURCE OF TRUTH.
// Every screen (quotation, job planning, public/renewal forms, the reference popups)
// derives the CT17 category and man-days from here. No screen keeps its own
// scope→industry map. Fixed scopes ALWAYS override generic / "Other Industries" logic.
//
// Locked fixed-scope rules (per CT17 reference, confirmed 2026-06):
//   Construction              ≤200: 1.0 · 201–500: 1.5 · >500: 2.0
//   Metalworking industry     any size: 1.0
//   Shipyard                  ≤500: 1.0 · >500: 2.0
//   Oil refinery / Petrochemical / Semiconductor / Chemical / Pharma / Bulk storage
//                             any size: 2.0
//   Other industries          High 1.0/1.5/2.0 · Medium 0.5/1.0/1.5 · Low 0.5/0.5/1.0
// ─────────────────────────────────────────────────────────────────────────────

const FLAT_HIGH_INDUSTRIES = [
  'Oil refinery / Petrochemical plant',
  'Semiconductor wafer fabrication plant',
  'Chemical manufacturing plant',
  'Pharmaceutical plant',
  'Bulk storage terminal',
]

// Maps every questionnaire audit_scope label (and any legacy industry string) to a canonical
// calculator key. Fixed scopes resolve here directly, so they never reach "Other Industries".
const SCOPE_TO_INDUSTRY = {
  'Construction Worksite': 'Construction',
  'Construction': 'Construction',
  'Metalworking Industry': 'Metalworking Industry',
  'Metalworking': 'Metalworking Industry',                 // legacy mis-mapping that caused the 0.5 bug
  'Shipyard': 'Shipyard',
  'Oil Refinery or Petrochemical Plant': 'Oil refinery / Petrochemical plant',
  'Oil refinery / Petrochemical plant': 'Oil refinery / Petrochemical plant',
  'Semiconductor Wafer Fabrication Plant': 'Semiconductor wafer fabrication plant',
  'Semiconductor wafer fabrication plant': 'Semiconductor wafer fabrication plant',
  'Chemical Manufacturing Plant': 'Chemical manufacturing plant',
  'Chemical manufacturing plant': 'Chemical manufacturing plant',
  'Pharmaceutical Plant': 'Pharmaceutical plant',
  'Pharmaceutical plant': 'Pharmaceutical plant',
  'Bulk Storage Terminal': 'Bulk storage terminal',
  'Bulk storage terminal': 'Bulk storage terminal',
}

// Normalise any industry/scope string to a canonical calculator key.
export function canonicalIndustry(s) {
  if (!s) return ''
  const t = String(s).trim()
  return SCOPE_TO_INDUSTRY[t] || t           // 'Other - *' values pass through unchanged
}

// THE single scope→industry resolver every screen must use.
// Fixed scope wins outright; only genuine "Other Industries" uses complexity.
export function resolveCT17Industry(scope, opts = {}) {
  const s = (scope || '').trim()
  if (s && s !== 'Other Industries' && SCOPE_TO_INDUSTRY[s]) return SCOPE_TO_INDUSTRY[s]
  if (s === 'Other Industries') {
    if (opts.complexityIndustry) return opts.complexityIndustry      // pre-classified or manual override
    return classifyComplexity(opts.ssicCode, opts.businessNature).industry
  }
  return canonicalIndustry(s)
}

// CT17 fixed-scope + complexity man-day reference — the ONE table the UI renders from
// (so a displayed reference can never drift from the calculator again).
export const CT17_MANDAY_TABLE = [
  { industry: 'Construction',          note: '—',             u200: '1.0', u500: '1.5', over: '2.0' },
  { industry: 'Metalworking industry', note: 'Any size: 1.0', u200: '1.0', u500: '1.0', over: '1.0' },
  { industry: 'Shipyard',              note: '—',             u200: '1.0', u500: '1.0', over: '2.0' },
  { industry: 'Oil refinery / Petrochemical / Semiconductor / Chemical / Pharma / Bulk storage', note: 'Any size: 2.0', u200: '2.0', u500: '2.0', over: '2.0' },
  { industry: 'Other Industries',      note: 'High',          u200: '1.0', u500: '1.5', over: '2.0' },
  { industry: 'Other Industries',      note: 'Medium',        u200: '0.5', u500: '1.0', over: '1.5' },
  { industry: 'Other Industries',      note: 'Low',           u200: '0.5', u500: '0.5', over: '1.0' },
]

export function calcBizSAFEMandays(industry, numEmployees, numSites = 1) {
  const emp = parseInt(numEmployees) || 0
  // A business operates from at least one location — never let a blank/"0"/non-numeric feed 0 sites into
  // the formula (which would under-count man-days). Floor to 1.
  const sites = Math.max(1, parseInt(numSites) || 1)
  const ind = canonicalIndustry(industry)
  let baseMandays

  if (ind === 'Construction') {
    if (emp <= 200) baseMandays = 1
    else if (emp <= 500) baseMandays = 1.5
    else baseMandays = 2
  } else if (ind === 'Metalworking Industry') {
    baseMandays = 1                                  // flat 1.0 — any manpower size
  } else if (ind === 'Shipyard') {
    baseMandays = emp <= 500 ? 1 : 2                 // ≤500: 1.0, >500: 2.0
  } else if (FLAT_HIGH_INDUSTRIES.includes(ind)) {
    baseMandays = 2                                  // flat 2.0 — any manpower size
  } else if (ind === 'Other - High complexity') {
    if (emp <= 200) baseMandays = 1
    else if (emp <= 500) baseMandays = 1.5
    else baseMandays = 2
  } else if (ind === 'Other - Medium complexity') {
    if (emp <= 200) baseMandays = 0.5
    else if (emp <= 500) baseMandays = 1
    else baseMandays = 1.5
  } else if (ind === 'Other - Low complexity') {
    if (emp <= 200) baseMandays = 0.5
    else if (emp <= 500) baseMandays = 0.5
    else baseMandays = 1
  } else {
    // Unresolved / Pending Review — conservative placeholder until complexity is confirmed.
    if (emp <= 200) baseMandays = 0.5
    else if (emp <= 500) baseMandays = 1
    else baseMandays = 1.5
  }

  // Multi-site — CT 17 SCHEME REQUIREMENT (overrides IAF MD 1 for bizSAFE RM): visit at least 10% of
  // worksites (min 1) and add 0.5 man-day per additional site visited. Do NOT replace with IAF MD 1's √x
  // site-sampling — for bizSAFE RM, CT 17 governs and mandates this 10% rule.
  const siteVisits = Math.ceil(sites * 0.1) || 1
  const totalMandays = baseMandays * (1 + (siteVisits - 1) * 0.5)

  return {
    baseMandays,
    numSitesToVisit: siteVisits,
    totalMandays: Math.round(totalMandays * 2) / 2,
    requiresLeadAuditor: emp > 200,
  }
}

export function calcSHMSMandays(scope, numEmployees, auditType = 'subsequent') {
  const emp = parseInt(numEmployees) || 0
  const isInitial = auditType === 'initial'

  if (scope === 'Construction Worksite') {
    if (isInitial) return { mandays: 2, requiresLeadAuditor: true, minTeamSize: 2 }
    if (emp <= 200) return { mandays: 3, requiresLeadAuditor: true, minTeamSize: 2 }
    if (emp <= 500) return { mandays: 4, requiresLeadAuditor: true, minTeamSize: 2 }
    return { mandays: 5, requiresLeadAuditor: true, minTeamSize: 2 }
  }

  if (scope === 'Metalworking Industry') {
    if (emp <= 50) return { mandays: isInitial ? 2 : 1, requiresLeadAuditor: true, minTeamSize: 2 }
    if (emp <= 99) return { mandays: isInitial ? 3 : 2, requiresLeadAuditor: true, minTeamSize: 2 }
    return { mandays: isInitial ? 5 : 2, requiresLeadAuditor: true, minTeamSize: 2 }
  }

  if (scope === 'Shipyard') {
    if (emp <= 199) return { mandays: isInitial ? 2 : 3, requiresLeadAuditor: true, minTeamSize: 2 }
    if (emp <= 500) return { mandays: isInitial ? 2 : 4, requiresLeadAuditor: true, minTeamSize: 2 }
    return { mandays: isInitial ? 2 : 5, requiresLeadAuditor: true, minTeamSize: 2 }
  }

  if (scope === 'Oil Refinery or Petrochemical Plant') {
    return { mandays: 4, requiresLeadAuditor: true, minTeamSize: 2, note: 'Minimum 4 man-days per IAF MD 5 high complexity' }
  }

  return { mandays: 2, requiresLeadAuditor: true, minTeamSize: 2 }
}

export function requiresLeadAuditorBizSAFE(numEmployees) {
  return parseInt(numEmployees) > 200
}

export function getMultiSiteSampling(totalSites, cycleHistory = []) {
  const minVisit = Math.ceil(totalSites * 0.1)
  return {
    minSitesToVisit: Math.max(minVisit, 1),
    note: 'At minimum 10% of total worksites must be visited. Sites visited must differ from previous 3-year cycle.'
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPLEXITY CLASSIFIER — for "Other Industries" only (INTERNAL knowledge)
// Mapped from the IAF / CT17 business-sector complexity table (High/Medium/Low).
// Matching is on the client's described BUSINESS NATURE only (SSIC is ignored —
// this is internal, not a SAC deliverable). Named scopes (Construction, Shipyard,
// Metalworking, etc.) have fixed CT17 man-days and never reach this function.
//
// Rules (agreed defaults):
//  - Highest hazard wins (High checked first) to stay conservative.
//  - Activity-qualified sectors are matched specifically
//    (transport: dangerous goods=High, passengers/non-dangerous=Medium, no fleet=Low).
//  - Sectors the table grades conditionally ("depending… could be high") → Pending Review.
//  - No confident match → Pending Review (confirm before quoting; manual override available).
//
// PROVISIONAL: keyword phrases transcribed from the supplied table — pending business sign-off.
// Returns: { complexity: 'High'|'Medium'|'Low'|'Pending Review', industry, reason }
// ─────────────────────────────────────────────────────────────────────────────
export function classifyComplexity(ssicCode, businessNature) {
  const biz = (businessNature || '').toLowerCase().trim()
  // match a keyword at a word start (avoids interior false positives e.g. "placement"→"cement",
  // "refinance"→"finance"), while still catching plurals/compounds e.g. "wooden", "it services")
  const m = (w) => new RegExp('\\b' + w.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i').test(biz)
  const has = (...words) => words.some(m)
  const out = (complexity, reason) => ({
    complexity,
    industry: complexity === 'High' ? 'Other - High complexity'
      : complexity === 'Medium' ? 'Other - Medium complexity'
      : complexity === 'Low' ? 'Other - Low complexity'
      : 'Other - Pending Review',
    reason,
  })

  if (!biz) return out('Pending Review', 'No business description provided — confirm complexity before quoting')

  // ── HIGH (table) — checked first; highest hazard wins ──
  const HIGH = [
    'offshore fishing', 'dredging', 'diving', 'mining', 'quarry',
    'coke', 'refined petroleum', 'petroleum product', 'oil and gas extraction', 'oil extraction', 'gas extraction',
    'tanning', 'pulping', 'pulp mill', 'paper recycling', 'oil refining', 'oil refinery', 'refinery',
    'chemical', 'pesticide', 'battery', 'batteries', 'accumulator', 'pharmaceutical', 'pharma',
    'fibreglass', 'fiberglass', 'gas production', 'gas storage', 'gas distribution',
    'electricity generation', 'electricity distribution', 'power generation', 'power plant',
    'nuclear', 'hazardous material', 'hazardous substance',
    'concrete', 'cement', 'plaster', 'primary production of metal', 'primary metal', 'smelting',
    'hot forming', 'cold forming', 'metal fabrication', 'forging',
    'metal structure', 'aerospace', 'automotive', 'weapon', 'explosive', 'ammunition',
    'hazardous waste', 'incineration', 'effluent', 'sewerage', 'sewage',
    'construction', 'demolition', 'building completion',
    'electrical installation', 'hydraulic installation', 'air conditioning installation', 'air-con installation', 'aircon installation',
    'slaughter', 'abattoir', 'dangerous goods', 'defence', 'defense', 'crisis management',
    'healthcare', 'hospital', 'veterinary', 'clinic', 'medical', 'social work',
  ]
  const hHit = HIGH.find(m)
  if (hHit) return out('High', `High-complexity sector per CT17 table (matched: "${hHit}")`)

  // ── Activity-qualified MEDIUM / LOW (checked before generic lists) ──
  if (has('transport', 'transportation') && has('passenger')) return out('Medium', 'Transport of passengers — Medium per CT17 table')
  if (has('transport', 'transportation') && has('non-dangerous')) return out('Medium', 'Transport of non-dangerous goods — Medium per CT17 table')
  if (has('industrial cleaning', 'hygiene cleaning', 'dry cleaning')) return out('Medium', 'Specialised cleaning — Medium per CT17 table')
  if (has('transport', 'transportation', 'distribution') && has('no fleet', 'no actual fleet', 'management service'))
    return out('Low', 'Transport/distribution management with no fleet — Low per CT17 table')

  // ── MEDIUM (table) ──
  const MEDIUM = [
    'aquaculture', 'fishing', 'food processing', 'food manufacturing', 'beverage production', 'tobacco',
    'textile', 'clothing', 'garment', 'apparel',
    'wood', 'wooden', 'timber', 'carpentry', 'joinery', 'woodworking', 'furniture', 'board manufacturing',
    'paper production', 'paper product', 'glass', 'clay',
    'mechanical engineering', 'engineering assembly', 'metallic product', 'metal product',
    'printed circuit', 'pcb', 'rubber', 'plastic injection', 'injection moulding', 'plastic moulding', 'plastic forming',
    'electrical equipment', 'electronic equipment', 'electronics assembly', 'electronics manufactur',
    'composting', 'landfill', 'water treatment', 'water purification', 'water abstraction', 'water distribution',
    'hotel', 'leisure', 'personal service',
  ]
  const mHit = MEDIUM.find(m)
  if (mHit) return out('Medium', `Medium-complexity sector per CT17 table (matched: "${mHit}")`)

  // ── LOW (table) ──
  const LOW = [
    'corporate', 'holding company', 'headquarters', 'head office',
    'business service', 'telecommunication', 'telecom', 'post office', 'postal',
    'restaurant', 'cafe', 'catering', 'camping', 'estate agency', 'estate management', 'real estate', 'property management',
    'public administration', 'local authority', 'government',
    'financial', 'finance', 'bank', 'insurance', 'advertising',
    'office', 'administrative', 'clerical', 'software', 'it service', 'information technology',
  ]
  const lHit = LOW.find(m)
  if (lHit) return out('Low', `Low-complexity sector per CT17 table (matched: "${lHit}")`)

  // ── CONDITIONAL / ambiguous sectors (table says "depends") → confirm before quoting ──
  const CONDITIONAL = [
    'farming', 'forestry', 'agriculture', 'transport equipment', 'fossil fuel', 'fuel wholesale', 'fuel retail',
    'wholesale', 'retail', 'engineering service', 'education', 'school', 'training', 'university',
    'research', 'laboratory', 'technical testing', 'ceramic',
    'transport', 'transportation', 'logistics', 'freight', 'cargo', 'recycling', 'cleaning',
  ]
  const cHit = CONDITIONAL.find(m)
  if (cHit) return out('Pending Review', `Sector graded conditionally in the CT17 table — confirm complexity before quoting (matched: "${cHit}")`)

  // ── No confident match ──
  return out('Pending Review', 'Unrecognised description — confirm complexity before quoting')
}

function fmtDateShort(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

// One-line basis text for the complexity display. Folds override who/when/reason into a
// single line, shows the table reason for system determinations, and neutralises any legacy
// stored reasons (older quotes saved "…Ops/QM to determine…" before this rewrite).
export function complexityBasisText(cc) {
  if (!cc) return ''
  if (cc.override_reason) {
    const who = cc.determined_by || 'reviewer'
    const when = fmtDateShort(cc.determined_at)
    return `Manual Override by ${who}${when ? ' · ' + when : ''}. Reason: ${cc.override_reason}`
  }
  const r = cc.reason || ''
  if (!r || /ops\/qm|insufficient description/i.test(r)) {
    return 'Not auto-classified — confirm complexity before quoting'
  }
  return r
}